import type { components } from '@/api/client';
import { narrowEnum, nullableString } from '@/api/mappers/_shared';
import type {
  AgeOfOnsetCategory,
  AllergyProfileViewModel,
  AllergyViewModel,
} from '@/features/patient-record/types';

type AllergyProfileDto = components['schemas']['AllergyProfile'];
type AllergyDto = components['schemas']['Allergy'];

const KNOWN_AGE_OF_ONSET: ReadonlySet<AgeOfOnsetCategory> =
  new Set<AgeOfOnsetCategory>([
    'unknown',
    'congenital',
    'antenatal',
    'neonatal',
    'pediatric',
    'adult',
  ]);

function nullableNumber(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export function mapAllergy(dto: AllergyDto): AllergyViewModel {
  return {
    id: dto.id ?? '',
    allergen: dto.allergen,
    notes: nullableString(dto.notes),
    ageOfOnsetCategory: narrowEnum<AgeOfOnsetCategory>(
      dto.age_of_onset_category,
      KNOWN_AGE_OF_ONSET,
      null,
    ),
    ageOfOnsetYears: nullableNumber(dto.age_of_onset_years),
    createdAt: nullableString(dto.created_at),
    createdBy: nullableString(dto.created_by),
    modifiedOn: nullableString(dto.modified_on),
    modifiedBy: nullableString(dto.modified_by),
  };
}

/**
 * BE returns either an empty body (FE http.ts surfaces as `undefined`)
 * or a fully-populated profile. The mapper collapses both to a single
 * view shape — empty list, NKDA off — so the UI only has one branch.
 */
export function mapAllergyProfile(
  dto: AllergyProfileDto | null | undefined,
): AllergyProfileViewModel {
  if (dto === undefined || dto === null) {
    return { id: null, nkda: false, allergies: [] };
  }
  return {
    id: dto.id ?? null,
    nkda: dto.nkda ?? false,
    allergies: (dto.allergies ?? []).map(mapAllergy),
  };
}
